import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { useAnimatedScrollHandler } from 'react-native-reanimated';
import Text from 'design-system/Text';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import Squircle from 'design-system/Squircle';
import StatusBar from 'design-system/StatusBar';
import StatusIcon from 'design-system/StatusIcon';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { AppView, Button, ButtonGroup, CurrencyAmountText, rem, SCROLL_INDICATOR_INSETS, Spacer } from 'design-system';
import IconEmmaWallet from 'design-system/icons/IconEmmaWallet';
import Links from 'utils/links';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import Modal from 'utils/packages/Modal';
import { useAppSelector } from 'store/hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { formatDateTime, formatDateInProgressTimeline as formatTimeInfoDate } from 'features/invest/utils';
import ProgressCard from '../components/InvestOnboardingFunnel/ProgressCard';
import { selectTradingAccount } from '../reducers/selectors';
import { AccountTypeDisplay, AccountTypeDisplayLong, IsaAllowanceDescription, JisaAllowanceDescription, } from '../constants';
import { isWeb, isUKTimezone } from '../../../constants';
import { useConnectedAccount } from '../hooks/selectors';
const strings = {
    depositRequest: 'Deposit request',
    rejected: 'Deposit rejected',
    requested: 'Deposit requested',
    depositRejected: 'Your deposit couldn’t be completed because it would take you over your ISA allowance for this tax year. No funds were taken and your money has been returned. ', // space is intentional
    depositRejectedJISA: 'Your deposit couldn’t be completed because it would take you over your JISA allowance for this tax year. No funds were taken and your money has been returned. ', // space is intentional
    depositIsPending: 'Your deposit is pending. It should be available within the next few hours. ', // space is intentional
    learnMore: 'Learn more',
    rejectInfoModalTitle: `${AccountTypeDisplay.ISA} allowance`,
    rejectInfoModalTitleJISA: `${AccountTypeDisplayLong.JISA} allowance`,
    okay: 'Okay',
};
const DepositToTradingAccountTimeline = withAnimatedNavigationHeader(({ scrollY, navigation, route }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const connectedAccount = useConnectedAccount();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerLeft: () => (<NavigationCloseButton inWebHeader onPress={() => {
                    navigation.pop();
                }}/>),
            headerBackVisible: false,
        });
    }, []);
    const { date, amount, rejected, depositMethod } = route.params;
    const tradingAccount = useAppSelector(selectTradingAccount);
    const accountType = tradingAccount?.account.accountType || 'GIA';
    const isJISA = accountType === 'JISA';
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = handle.contentOffset.y;
    });
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, undefined, paddingHorizontalStyle);
    const handleClose = useCallback(() => {
        navigation.goBack();
    }, []);
    const openDepositDoc = useCallback(() => {
        Links.openLink('https://emma-app.com/learn-more/deposit-to-seccl');
    }, []);
    const showRejectInfoModal = useCallback(() => {
        Modal.showConfirmation({
            title: isJISA ? strings.rejectInfoModalTitleJISA : strings.rejectInfoModalTitle,
            subtitle: isJISA ? JisaAllowanceDescription : IsaAllowanceDescription,
            confirmButtonTitle: strings.okay,
        });
    }, [isJISA]);
    const content = useMemo(() => {
        if (depositMethod?.method === 'singleDirectDebit') {
            const collectionDate = (depositMethod?.dateOfCollection || depositMethod?.estimatedDateOfCollection);
            const dateTimeString = formatDateTime(collectionDate);
            const estimatedTimeInfo = `Estimated by ${dateTimeString}${isUKTimezone ? '' : ' UK time'}`;
            return (<>
            <ProgressCard lightBg completed noTextDecoration alwaysShowTimeline alwaysShowChildren title={strings.requested} timeInfo={formatTimeInfoDate(date)}>
              <Text TextThin-14 Secondary>
                We’ve sent the Direct Debit request to your bank.
              </Text>
            </ProgressCard>
            <ProgressCard lightBg isActiveStep noTextDecoration completed={false} alwaysShowTimeline isFailure={rejected} lastItem={rejected} title={`Collecting from ${connectedAccount ? connectedAccount?.bankName : 'linked bank'}`} timeInfo={rejected ? undefined : 'Now'}>
              <Text TextThin-14 Secondary>
                We’re collecting the payment. This usually takes 5 working days.
              </Text>
            </ProgressCard>
            {!rejected && (<ProgressCard lightBg lastItem noTextDecoration completed={false} isActiveStep={false} isFailure={rejected} title={`Arrives in ${AccountTypeDisplayLong[accountType]}`} timeInfo={estimatedTimeInfo}>
                <Text TextThin-14 Secondary>
                  Once your funds arrive, they’ll start earning interest. You can invest or withdraw at any time.
                </Text>
              </ProgressCard>)}
          </>);
        }
        return (<>
          <ProgressCard lightBg completed noTextDecoration alwaysShowTimeline title={strings.requested} timeInfo={formatTimeInfoDate(date)}/>
          <ProgressCard lightBg lastItem isActiveStep isFailure={rejected} completed={false} title={`Arriving in ${AccountTypeDisplayLong[accountType]}`}>
            <Text TextThin-14 Secondary>
              {/* eslint-disable-next-line no-nested-ternary */}
              {rejected ? (isJISA ? strings.depositRejectedJISA : strings.depositRejected) : strings.depositIsPending}
              <Text Primary Text-14 suppressHighlighting onPress={rejected ? showRejectInfoModal : openDepositDoc}>
                {strings.learnMore}
              </Text>
            </Text>
          </ProgressCard>
        </>);
    }, [
        accountType,
        connectedAccount,
        date,
        depositMethod?.dateOfCollection,
        depositMethod?.estimatedDateOfCollection,
        depositMethod?.method,
        isJISA,
        openDepositDoc,
        rejected,
        showRejectInfoModal,
    ]);
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} keyboardDismissMode="on-drag" contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <Spacer h={24}/>
          <View style={styles.amountInfo}>
            <View>
              <View style={styles.iconContainer}>
                <IconEmmaWallet size={rem(52)}/>
              </View>
              <View style={styles.statusIconContainer}>
                <Squircle width={rem(24)} height={rem(24)} color={colors.cards.onDark}>
                  <StatusIcon state={rejected ? 'cancelled-red' : 'in-progress'} height={20} width={20}/>
                </Squircle>
              </View>
            </View>
            <Spacer h={24}/>
            <Text Text-14 Secondary>
              {rejected ? strings.rejected : strings.depositRequest}
            </Text>
            <View>
              <CurrencyAmountText size="XL" value={{ amount, currency: 'GBP' }}/>
              {!!rejected && <View style={styles.strikeThrough}/>}
            </View>
          </View>
          <Spacer h={24}/>
          {content}
          <Spacer h={24}/>
        </Animated.ScrollView>
        <ButtonGroup type="floating">
          <Button title="Close" brand onPress={handleClose}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    title: ``,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    amountInfo: {
        alignItems: 'center',
    },
    iconContainer: {
        width: rem(64),
        height: rem(64),
        borderRadius: rem(64),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.cards.onDark,
    },
    statusIconContainer: {
        right: 0,
        bottom: 0,
        position: 'absolute',
    },
    strikeThrough: {
        position: 'absolute',
        top: '55%',
        height: StyleSheet.hairlineWidth,
        left: -rem(4),
        right: -rem(4),
        backgroundColor: colors.text.primary,
    },
}));
export default DepositToTradingAccountTimeline;
